import { MEASURE_SLUGS } from "~/consts";
import { Filter } from "~/modules/filters/types";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";
import { getAllTokensStackedChart } from "~/modules/token_timeseries/shared/getAllTokensStackedChart";
import { getMintBurnVolumeChart } from "~/modules/token_timeseries/shared/getMintBurnVolumeChart";
import { getActiveAddressesChart } from "~/modules/token_timeseries/shared/getActiveAddressesChart";
import { getTransferVolumeChart } from "~/modules/token_timeseries/shared/getTransferVolumeChart";
import { getHoldersChart } from "~/modules/token_timeseries/shared/getHoldersChart";
import { getPriceChart } from "~/modules/asset_timeseries_v4/shared/getPriceChart";
import { getNetAssetValueChart } from "~/modules/asset_timeseries_v4/shared/getNetAssetValueChart";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import { VisualizationMode } from "~/modules/visualization/components/Timeseries/types";

// // // //

export function getCommoditiesTimeSeriesModes(props: {
    individual?: boolean;
    filters: Filter[];
    assetTimeSeriesFilters?: Filter[];
    defaultGroupBy: TokenTimeseriesGroupBy;
    enableGroupByControls: boolean;
}): VisualizationMode[] {
    const {
        individual = false,
        filters,
        assetTimeSeriesFilters = [],
        defaultGroupBy,
        enableGroupByControls,
    } = props;

    // Define VisualizationModes
    const CIRCULATING_MARKET_VALUE = getAllTokensStackedChart({
        key: "commodities-market-value",
        label: "Market Caps",
        description: "",
        filters,
        defaultGroupBy,
        enableGroupByControls,
        ignoreDownloadsFeatureFlag: true,
        measureSlug: MEASURE_SLUGS.bridged_token_value_dollar,
        groupByOptions: [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.network,
        ],
    });

    const NET_ASSET_VALUE_DOLLAR_BASE = getNetAssetValueChart({
        key: "commodities-net-asset-value",
        label: "Net Asset Value",
        filters,
        defaultGroupBy: AssetTimeseriesGroupBy.asset,
        enableGroupByControls,
    });

    const NET_ASSET_VALUE_DOLLAR = {
        ...NET_ASSET_VALUE_DOLLAR_BASE,
        visualizationConfig: {
            ...NET_ASSET_VALUE_DOLLAR_BASE.visualizationConfig,
            yAxis: {
                min: undefined,
            },
        },
    };

    const PRICE = getPriceChart({
        key: "commodities-price",
        label: "Price",
        filters: assetTimeSeriesFilters,
        enableGroupByControls: false,
    });

    const MINT_BURN_VOLUME = getMintBurnVolumeChart({
        key: "commodities-fund-flows",
        filters,
    });
    const ACTIVE_ADDRESSES = getActiveAddressesChart({
        key: "commodities-active-addresses",
        filters,
        defaultGroupBy: TokenTimeseriesGroupBy.network,
        enableGroupByControls,
        groupByOptions: [
            TokenTimeseriesGroupBy.measure,
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.protocol,
        ],
    });
    const TRANSFER_VOLUME = getTransferVolumeChart({
        key: "commodities-transfer-volume",
        filters,
        defaultGroupBy: TokenTimeseriesGroupBy.network,
        enableGroupByControls,
        groupByOptions: [
            TokenTimeseriesGroupBy.measure,
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.protocol,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.peggedAsset,
            TokenTimeseriesGroupBy.jurisdictionCountry,
        ],
    });
    const HOLDERS = getHoldersChart({
        key: "commodities-holders",
        filters,
        defaultGroupBy,
        enableGroupByControls,
        groupByOptions: [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.protocol,
            TokenTimeseriesGroupBy.peggedAsset,
            TokenTimeseriesGroupBy.jurisdictionCountry,
        ],
    });

    delete MINT_BURN_VOLUME.content.description;

    const modes = individual
        ? [
              CIRCULATING_MARKET_VALUE,
              NET_ASSET_VALUE_DOLLAR,
              TRANSFER_VOLUME,
              ACTIVE_ADDRESSES,
              PRICE,
              MINT_BURN_VOLUME,
              HOLDERS,
          ]
        : [
              CIRCULATING_MARKET_VALUE,
              TRANSFER_VOLUME,
              ACTIVE_ADDRESSES,
              MINT_BURN_VOLUME,
              HOLDERS,
          ];

    return modes;
}
